// Zustand store for the Lex runtime chat surface.
// Phase 5 polish: chat_session_id persists in localStorage so a page refresh
// keeps the same session (the runtime hydrates state from Postgres for that
// id). focus_matter_id is also tracked here so the UI can offer a click-to-
// switch on the Suggested-focus panel; on the next request the value flows
// through `context.selected_matter_id` and the resolver's
// `select_focus_matter` priority puts it ahead of the recommended focus.

import { create } from 'zustand';

import { useAppStore } from '@/store/appStore';
import { getMyModelPolicy, LexRuntimeError, postChatResume, postChatStream } from '../api/lexRuntimeClient';
import type {
  ChatRuntimeResponse,
  ConversationEntry,
  DmsDocumentAttachmentSelection,
  GovernedLexModel,
  LexDocumentAttachment,
  LexSelectionContext,
  RuntimeStreamEvent,
  RuntimeStreamProgress,
} from '../types/runtime';
import { extractDocumentTextFromFile } from '../utils/documentText';

const SESSION_STORAGE_KEY = 'edgelex.lexRuntime.chatSessionId';
const FOCUS_STORAGE_KEY = 'edgelex.lexRuntime.focusMatterId';
const MODEL_STORAGE_KEY = 'edgelex.lexRuntime.selectedModelId';
const DOCUMENT_ACTION_PROMPTS = {
  summary: 'Summarize the selected document.',
  detailed_analysis: 'Provide a detailed legal analysis of the selected document. Focus on issues, obligations, risks, missing facts, and practical next steps.',
  key_issues: 'Identify the key legal and factual issues in the selected document.',
  dates_deadlines: 'Extract important dates, deadlines, notice periods, and time-sensitive obligations from the selected document.',
  risks: 'Analyze the selected document for legal, procedural, evidentiary, and drafting risks.',
  follow_up_questions: 'Draft follow-up questions I should ask before relying on or filing this document.',
} as const;

function fileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

function newId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

function readPersistedSessionId(): string {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return newId('session');
    }
    const stored = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored && stored.trim()) {
      return stored;
    }
    const fresh = newId('session');
    window.localStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return newId('session');
  }
}

function writePersistedSessionId(value: string): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(SESSION_STORAGE_KEY, value);
  } catch {
    /* ignore quota / private mode */
  }
}

function readPersistedFocus(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(FOCUS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePersistedFocus(value: string | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (value) {
      window.localStorage.setItem(FOCUS_STORAGE_KEY, value);
    } else {
      window.localStorage.removeItem(FOCUS_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function readPersistedModelId(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(MODEL_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePersistedModelId(value: string | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (value) {
      window.localStorage.setItem(MODEL_STORAGE_KEY, value);
    } else {
      window.localStorage.removeItem(MODEL_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function currentRuntimeAuthContext(fallback: Pick<LexRuntimeState, 'realmId' | 'userId'>): {
  realmId: string;
  userId: string;
} {
  const appState = useAppStore.getState();
  return {
    realmId: appState.currentFirmId ?? fallback.realmId,
    userId: appState.currentUserId ?? fallback.userId,
  };
}

function attachmentKey(attachment: Pick<LexDocumentAttachment, 'source' | 'documentId' | 'versionId' | 'id'>): string {
  if (attachment.source === 'dms') {
    return `dms:${attachment.documentId ?? ''}:${attachment.versionId ?? ''}`;
  }
  return attachment.id;
}

function revokeAttachmentPreview(attachment: LexDocumentAttachment): void {
  if (!attachment.previewObjectUrl) return;
  try {
    URL.revokeObjectURL(attachment.previewObjectUrl);
  } catch {
    /* object URL may already be gone */
  }
}

function documentAckMessage(attachment: LexDocumentAttachment): string {
  const mode = attachment.source === 'dms'
    ? 'DMS document'
    : attachment.textAvailable
      ? 'local document text'
      : 'local file metadata';
  const textStatus = attachment.source === 'local' && !attachment.textAvailable
    ? ' I do not have readable text for this file yet, so detailed answers will need a DMS-ingested copy or a text/PDF file.'
    : '';
  return `Loaded ${mode}: ${attachment.title}.${textStatus} I can summarize it, identify key issues, analyze risks, extract dates and deadlines, or draft follow-up questions.`;
}

function buildDocumentRuntimeContext(
  focusMatterId: string | null,
  attachments: LexDocumentAttachment[],
  selectedModel: GovernedLexModel | null,
  selectionContext?: LexSelectionContext | null,
  comparisonSelection?: LexSelectionContext | null,
): Record<string, unknown> {
  const dmsAttachments = attachments.filter((attachment) => attachment.source === 'dms' && attachment.documentId);
  const ephemeralAttachment = dmsAttachments.length === 0
    ? attachments.find((attachment) => attachment.source === 'local' && attachment.textAvailable)
    : undefined;
  const selectedDocumentIds = [
    ...dmsAttachments.map((attachment) => attachment.documentId),
    ...(ephemeralAttachment ? [ephemeralAttachment.documentId ?? ephemeralAttachment.id] : []),
  ].filter((value): value is string => typeof value === 'string' && value.length > 0);

  const visibleEntities = attachments.map((attachment, index) => ({
    id: attachment.documentId ?? attachment.id,
    document_id: attachment.documentId ?? attachment.id,
    version_id: attachment.versionId ?? undefined,
    document_version_id: attachment.versionId ?? undefined,
    matter_id: attachment.matterId ?? focusMatterId ?? undefined,
    client_id: attachment.clientId ?? undefined,
    label: attachment.title,
    title: attachment.title,
    type: attachment.source === 'dms' ? 'dms_document' : 'ephemeral_local_document',
    domain: 'documents',
    source: attachment.source === 'dms' ? 'lex_chat_dms_attach' : 'lex_chat_local_attach',
    transient: attachment.transient,
    ordinal: index + 1,
    metadata: {
      document_id: attachment.documentId ?? attachment.id,
      document_version_id: attachment.versionId ?? undefined,
      version_id: attachment.versionId ?? undefined,
      title: attachment.title,
      mime_type: attachment.mimeType ?? undefined,
      size_bytes: attachment.sizeBytes ?? undefined,
    },
  }));

  const context: Record<string, unknown> = {
    surface: 'browser-chat',
    source_surface: 'browser-chat',
    selected_document_ids: selectedDocumentIds,
    visible_entities: visibleEntities,
    candidate_sets: {
      documents: {
        items: visibleEntities,
        source: 'lex_chat_attached_documents',
      },
    },
    document_context: {
      active_documents: attachments.map((attachment) => ({
        id: attachment.documentId ?? attachment.id,
        document_id: attachment.documentId ?? attachment.id,
        version_id: attachment.versionId ?? undefined,
        title: attachment.title,
        source: attachment.source,
        transient: attachment.transient,
        text_available: attachment.textAvailable,
      })),
    },
  };

  if (selectedModel) {
    context.requested_model = {
      model_id: selectedModel.modelId,
      model_key: selectedModel.modelKey,
      provider_key: selectedModel.providerKey,
      display_name: selectedModel.displayName,
      source: 'browser_chat_model_selector',
    };
  }

  if (focusMatterId) {
    context.selected_matter_id = focusMatterId;
    context.recommended_focus = focusMatterId;
  }

  if (selectionContext?.text) {
    context.selection_text = selectionContext.text;
    context.selection_context = {
      id: selectionContext.id,
      source_surface: selectionContext.sourceSurface,
      label: selectionContext.label ?? undefined,
      document_id: selectionContext.documentId ?? undefined,
      document_version_id: selectionContext.versionId ?? undefined,
      version_id: selectionContext.versionId ?? undefined,
      document_title: selectionContext.documentTitle ?? undefined,
      matter_id: selectionContext.matterId ?? focusMatterId ?? undefined,
      client_id: selectionContext.clientId ?? undefined,
      page: selectionContext.page ?? undefined,
      evidence_id: selectionContext.evidenceId ?? undefined,
      citation_id: selectionContext.citationId ?? undefined,
      message_id: selectionContext.messageId ?? undefined,
      answer_kind: selectionContext.answerKind ?? undefined,
      metadata: selectionContext.metadata ?? undefined,
    };
  }

  if (comparisonSelection?.text) {
    context.comparison_selection = {
      id: comparisonSelection.id,
      text: comparisonSelection.text,
      source_surface: comparisonSelection.sourceSurface,
      label: comparisonSelection.label ?? undefined,
      document_id: comparisonSelection.documentId ?? undefined,
      document_version_id: comparisonSelection.versionId ?? undefined,
      version_id: comparisonSelection.versionId ?? undefined,
      document_title: comparisonSelection.documentTitle ?? undefined,
      matter_id: comparisonSelection.matterId ?? focusMatterId ?? undefined,
      client_id: comparisonSelection.clientId ?? undefined,
      page: comparisonSelection.page ?? undefined,
      evidence_id: comparisonSelection.evidenceId ?? undefined,
      citation_id: comparisonSelection.citationId ?? undefined,
      message_id: comparisonSelection.messageId ?? undefined,
      answer_kind: comparisonSelection.answerKind ?? undefined,
      metadata: comparisonSelection.metadata ?? undefined,
    };
  }

  if (dmsAttachments.length === 1) {
    const attachment = dmsAttachments[0];
    context.resolved_document_reference = {
      kind: 'document',
      document_id: attachment.documentId,
      document_version_id: attachment.versionId,
      version_id: attachment.versionId,
      matter_id: attachment.matterId ?? focusMatterId ?? undefined,
      label: attachment.title,
      title: attachment.title,
    };
  }

  if (ephemeralAttachment) {
    context.word_document = {
      document_id: ephemeralAttachment.documentId ?? ephemeralAttachment.id,
      version_id: ephemeralAttachment.versionId ?? undefined,
      matter_id: ephemeralAttachment.matterId ?? focusMatterId ?? undefined,
      body_snippet: ephemeralAttachment.bodySnippet,
      transient: true,
      source: 'ephemeral_word_document',
      title: ephemeralAttachment.title,
    };
  }

  return context;
}

const NODE_PROGRESS_LABELS: Record<string, { started: string; completed: string }> = {
  resolve_model_governance: {
    started: 'Resolving governed model',
    completed: 'Resolved governed model',
  },
  plan_document_query: {
    started: 'Planning document retrieval',
    completed: 'Planned document retrieval',
  },
  search_weaviate: {
    started: 'Searching document evidence',
    completed: 'Searched document evidence',
  },
  verify_chunks: {
    started: 'Verifying document chunks',
    completed: 'Verified document chunks',
  },
  assemble_evidence: {
    started: 'Assembling evidence',
    completed: 'Assembled evidence',
  },
  evaluate_citations: {
    started: 'Validating citations',
    completed: 'Validated citations',
  },
  build_document_answer: {
    started: 'Calling governed model',
    completed: 'Called governed model',
  },
  synthesize_answer: {
    started: 'Synthesizing answer',
    completed: 'Synthesized answer',
  },
  persist_state: {
    started: 'Saving graph state',
    completed: 'Saved graph state',
  },
  'deep_corpus_reasoning.resolve_corpus_scope': {
    started: 'Resolving deep-corpus scope',
    completed: 'Resolved deep-corpus scope',
  },
  'deep_corpus_reasoning.create_corpus_snapshot': {
    started: 'Creating corpus snapshot',
    completed: 'Created corpus snapshot',
  },
  'deep_corpus_reasoning.plan_recursive_reasoning': {
    started: 'Planning deep-corpus reasoning',
    completed: 'Planned deep-corpus reasoning',
  },
  'deep_corpus_reasoning.retrieve_candidate_chunks': {
    started: 'Retrieving candidate chunks',
    completed: 'Retrieved candidate chunks',
  },
  'deep_corpus_reasoning.inspect_corpus': {
    started: 'Inspecting corpus coverage',
    completed: 'Inspected corpus coverage',
  },
  'deep_corpus_reasoning.run_recursive_batches': {
    started: 'Running deep-corpus batch reasoning',
    completed: 'Completed deep-corpus batch reasoning',
  },
  'deep_corpus_reasoning.record_findings': {
    started: 'Recording deep-corpus findings',
    completed: 'Recorded deep-corpus findings',
  },
  'deep_corpus_reasoning.request_edgecite_validation': {
    started: 'Validating evidence with EdgeCite',
    completed: 'Validated evidence with EdgeCite',
  },
  'deep_corpus_reasoning.assemble_deep_reasoning_evidence': {
    started: 'Assembling deep-corpus evidence',
    completed: 'Assembled deep-corpus evidence',
  },
  'deep_corpus_reasoning.evaluate_sufficiency': {
    started: 'Evaluating evidence sufficiency',
    completed: 'Evaluated evidence sufficiency',
  },
  'deep_corpus_reasoning.prepare_answer_handoff': {
    started: 'Preparing answer handoff',
    completed: 'Prepared answer handoff',
  },
  'deep_corpus_reasoning.route_policy': {
    started: 'Applying deep-corpus answer policy',
    completed: 'Applied deep-corpus answer policy',
  },
  'deep_corpus_reasoning.prepare_repair_loop': {
    started: 'Preparing evidence repair pass',
    completed: 'Prepared evidence repair pass',
  },
  'deep_corpus_reasoning.synthesize_answer': {
    started: 'Synthesizing deep-corpus answer',
    completed: 'Synthesized deep-corpus answer',
  },
};

function streamProgressFromEvent(event: RuntimeStreamEvent): RuntimeStreamProgress | null {
  if (event.event_type === 'graph.started') {
    return { label: 'Starting LangGraph run', eventType: event.event_type };
  }
  if (event.event_type === 'answer.completed') {
    return { label: 'Answer ready', eventType: event.event_type };
  }
  if (event.event_type !== 'graph.node.started' && event.event_type !== 'graph.node.completed') return null;
  const status = event.event_type === 'graph.node.started' ? 'started' : 'completed';
  const nodeName = event.node_name ?? '';
  const labels = NODE_PROGRESS_LABELS[nodeName];
  return {
    label: labels?.[status] ?? `${status === 'started' ? 'Running' : 'Completed'} ${nodeName || 'graph node'}`,
    eventType: event.event_type,
    nodeName: event.node_name,
  };
}

export interface LexRuntimeState {
  realmId: string;
  userId: string;
  chatSessionId: string;
  focusMatterId: string | null;
  attachedDocuments: LexDocumentAttachment[];
  availableModels: GovernedLexModel[];
  selectedModelId: string | null;
  modelPolicyLoading: boolean;
  modelPolicyError: string | null;

  messages: ConversationEntry[];
  lastTurn: ChatRuntimeResponse | null;
  isLoading: boolean;
  streamProgress: RuntimeStreamProgress[];
  streamingDraft: string;
  error: string | null;

  sendMessage: (
    text: string,
    selectionContext?: LexSelectionContext | null,
    comparisonSelection?: LexSelectionContext | null,
  ) => Promise<void>;
  loadModelPolicy: () => Promise<void>;
  setAuthContext: (context: { realmId: string | null; userId: string | null }) => void;
  setSelectedModel: (modelId: string | null) => void;
  attachDmsDocument: (selection: DmsDocumentAttachmentSelection) => Promise<boolean>;
  attachLocalFiles: (files: File[]) => Promise<void>;
  removeAttachedDocument: (attachmentId: string) => void;
  sendDocumentAction: (action: keyof typeof DOCUMENT_ACTION_PROMPTS) => Promise<void>;
  setDocumentContextFromEditor: (document: DmsDocumentAttachmentSelection) => void;
  resumeWithDecision: (approval: 'approved' | 'rejected', basis?: string) => Promise<void>;
  resumeWithInteraction: (decision: Record<string, unknown>) => Promise<void>;
  resetSession: () => void;
  setFocusMatter: (matterId: string | null) => void;
}

export const useLexRuntimeStore = create<LexRuntimeState>((set, get) => ({
  realmId: '',
  userId: '',
  chatSessionId: readPersistedSessionId(),
  focusMatterId: readPersistedFocus(),
  attachedDocuments: [],
  availableModels: [],
  selectedModelId: readPersistedModelId(),
  modelPolicyLoading: false,
  modelPolicyError: null,

  messages: [],
  lastTurn: null,
  isLoading: false,
  streamProgress: [],
  streamingDraft: '',
  error: null,

  async loadModelPolicy() {
    if (get().modelPolicyLoading) return;
    set({ modelPolicyLoading: true, modelPolicyError: null });
    try {
      const policy = await getMyModelPolicy();
      const allowedModels = policy.allowedModels ?? [];
      const persisted = get().selectedModelId;
      const defaultModel = allowedModels.find((model) => model.isDefault) ?? allowedModels[0] ?? null;
      const selectedStillAllowed = persisted
        ? allowedModels.some((model) => model.modelId === persisted)
        : false;
      const selectedModelId = selectedStillAllowed ? persisted : defaultModel?.modelId ?? null;
      if (selectedModelId !== persisted) {
        writePersistedModelId(selectedModelId);
      }
      set({
        availableModels: allowedModels,
        selectedModelId,
        modelPolicyLoading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load model policy';
      set({ modelPolicyLoading: false, modelPolicyError: message });
    }
  },

  setSelectedModel(modelId: string | null) {
    const allowed = get().availableModels;
    const selectedModelId = modelId && allowed.some((model) => model.modelId === modelId)
      ? modelId
      : null;
    writePersistedModelId(selectedModelId);
    set({ selectedModelId });
  },

  setAuthContext(context: { realmId: string | null; userId: string | null }) {
    set({
      realmId: context.realmId ?? '',
      userId: context.userId ?? '',
    });
  },

  async sendMessage(text: string, selectionContext?: LexSelectionContext | null, comparisonSelection?: LexSelectionContext | null) {
    const trimmed = text.trim();
    if (!trimmed || get().isLoading) return;

    const userEntry: ConversationEntry = {
      id: newId('msg'),
      role: 'user',
      content: trimmed,
    };
    set((state) => ({
      messages: [...state.messages, userEntry],
      isLoading: true,
      streamProgress: [],
      streamingDraft: '',
      error: null,
    }));

    try {
      const focus = get().focusMatterId;
      const attachedDocuments = get().attachedDocuments;
      const selectedModel = get().availableModels.find((model) => model.modelId === get().selectedModelId) ?? null;
      const authContext = currentRuntimeAuthContext(get());
      const turn = await postChatStream(
        {
          message: trimmed,
          realm_id: authContext.realmId,
          user_id: authContext.userId,
          chat_session_id: get().chatSessionId,
          context: buildDocumentRuntimeContext(focus, attachedDocuments, selectedModel, selectionContext, comparisonSelection),
        },
        (event) => {
          if (event.event_type === 'message.delta') {
            const token = event.payload.token;
            if (typeof token === 'string' && token.length > 0) {
              set((state) => ({ streamingDraft: `${state.streamingDraft}${token}` }));
            }
          }
          const progress = streamProgressFromEvent(event);
          if (!progress) return;
          set((state) => ({
            streamProgress: [...state.streamProgress.slice(-5), progress],
          }));
        },
      );
      const lexEntry: ConversationEntry = {
        id: newId('msg'),
        role: 'lex',
        content: turn.answer.content,
        turn,
      };
      // Sync focusMatterId from the runtime's resolved focus so a fresh
      // session (where focus came from `recommended_focus` server-side) shows
      // the right alternatives panel without an explicit click.
      const resolvedFocus = turn.focus_matter_id ?? focus ?? null;
      set((state) => ({
        messages: [...state.messages, lexEntry],
        lastTurn: turn,
        focusMatterId: resolvedFocus,
        isLoading: false,
        streamProgress: [],
        streamingDraft: '',
      }));
      if (resolvedFocus !== focus) {
        writePersistedFocus(resolvedFocus);
      }
    } catch (err) {
      const message =
        err instanceof LexRuntimeError
          ? `${err.message}${err.detail ? `: ${err.detail}` : ''}`
          : err instanceof Error
            ? err.message
            : 'unknown error';
      set({ isLoading: false, streamProgress: [], streamingDraft: '', error: message });
    }
  },

  async attachDmsDocument(selection: DmsDocumentAttachmentSelection) {
    const attachment: LexDocumentAttachment = {
      id: `dms-${selection.documentId}-${selection.versionId}`,
      source: 'dms',
      title: selection.title,
      documentId: selection.documentId,
      versionId: selection.versionId,
      versionLabel: selection.versionLabel ?? null,
      matterId: selection.matterId ?? null,
      clientId: selection.clientId ?? null,
      transient: false,
      textAvailable: true,
    };
    set((state) => {
      const key = attachmentKey(attachment);
      const alreadyAttached = state.attachedDocuments.some((existing) => attachmentKey(existing) === key);
      return {
        attachedDocuments: alreadyAttached
          ? state.attachedDocuments
          : [...state.attachedDocuments, attachment],
        messages: [
          ...state.messages,
          {
            id: newId('msg'),
            role: 'lex',
            content: alreadyAttached
              ? `${attachment.title} is already loaded in this Lex session.`
              : documentAckMessage(attachment),
          },
        ],
      };
    });
    return true;
  },

  async attachLocalFiles(files: File[]) {
    const attachments = await Promise.all(
      files.map(async (file) => {
        const bodySnippet = await extractDocumentTextFromFile(file);
        const extension = fileExtension(file.name);
        const mimeType = file.type || null;
        let previewType: LexDocumentAttachment['previewType'] = bodySnippet ? 'text' : 'metadata';
        let previewData: Uint8Array | null = null;
        let previewObjectUrl: string | null = null;
        let previewText: string | null = bodySnippet;

        if (mimeType?.startsWith('image/')) {
          previewType = 'image';
          previewObjectUrl = URL.createObjectURL(file);
        } else if (mimeType === 'application/pdf' || extension === 'pdf') {
          try {
            previewType = 'pdf';
            previewData = new Uint8Array(await file.arrayBuffer());
          } catch {
            previewType = bodySnippet ? 'text' : 'metadata';
          }
        } else if (bodySnippet) {
          previewType = 'text';
          previewText = bodySnippet;
        }

        const attachment: LexDocumentAttachment = {
          id: newId('local-doc'),
          source: 'local',
          title: file.name,
          documentId: `local-${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`,
          mimeType,
          sizeBytes: file.size,
          bodySnippet,
          previewType,
          previewData,
          previewObjectUrl,
          previewText,
          transient: true,
          textAvailable: Boolean(bodySnippet),
        };
        return attachment;
      }),
    );

    set((state) => {
      const nextAttachments = [...state.attachedDocuments, ...attachments];
      const ackEntries: ConversationEntry[] = attachments.map((attachment) => ({
        id: newId('msg'),
        role: 'lex',
        content: documentAckMessage(attachment),
      }));
      return {
        attachedDocuments: nextAttachments,
        messages: [...state.messages, ...ackEntries],
      };
    });
  },

  removeAttachedDocument(attachmentId: string) {
    set((state) => ({
      attachedDocuments: state.attachedDocuments.filter((attachment) => {
        if (attachment.id === attachmentId) {
          revokeAttachmentPreview(attachment);
          return false;
        }
        return true;
      }),
    }));
  },

  async sendDocumentAction(action: keyof typeof DOCUMENT_ACTION_PROMPTS) {
    await get().sendMessage(DOCUMENT_ACTION_PROMPTS[action]);
  },

  setDocumentContextFromEditor(document: DmsDocumentAttachmentSelection) {
    const attachment: LexDocumentAttachment = {
      id: `dms-${document.documentId}-${document.versionId}`,
      source: 'dms',
      title: document.title,
      documentId: document.documentId,
      versionId: document.versionId,
      versionLabel: document.versionLabel ?? null,
      matterId: document.matterId ?? null,
      clientId: document.clientId ?? null,
      transient: false,
      textAvailable: true,
    };
    set((state) => {
      const key = attachmentKey(attachment);
      const alreadyAttached = state.attachedDocuments.some((existing) => attachmentKey(existing) === key);
      return {
        attachedDocuments: alreadyAttached
          ? state.attachedDocuments
          : [...state.attachedDocuments, attachment],
      };
    });
  },

  async resumeWithDecision(approval: 'approved' | 'rejected', basis?: string) {
    if (get().isLoading) return;
    const lastTurn = get().lastTurn;
    if (!lastTurn?.pending_approval) return;

    set({ isLoading: true, streamingDraft: '', error: null });

    try {
      const authContext = currentRuntimeAuthContext(get());
      const turn = await postChatResume({
        chat_session_id: get().chatSessionId,
        realm_id: authContext.realmId,
        user_id: authContext.userId,
        approval: { approval, basis },
      });
      const lexEntry: ConversationEntry = {
        id: newId('msg'),
        role: 'lex',
        content: turn.answer.content,
        turn,
      };
      const resolvedFocus = turn.focus_matter_id ?? get().focusMatterId ?? null;
      set((state) => ({
        messages: [...state.messages, lexEntry],
        lastTurn: turn,
        focusMatterId: resolvedFocus,
        isLoading: false,
        streamingDraft: '',
      }));
      if (resolvedFocus !== get().focusMatterId) {
        writePersistedFocus(resolvedFocus);
      }
    } catch (err) {
      const message =
        err instanceof LexRuntimeError
          ? `${err.message}${err.detail ? `: ${err.detail}` : ''}`
          : err instanceof Error
            ? err.message
            : 'unknown error';
      set({ isLoading: false, streamingDraft: '', error: message });
    }
  },

  async resumeWithInteraction(decision: Record<string, unknown>) {
    if (get().isLoading) return;
    const lastTurn = get().lastTurn;
    if (!lastTurn?.pending_interaction) return;

    set({ isLoading: true, streamingDraft: '', error: null });

    try {
      const authContext = currentRuntimeAuthContext(get());
      const turn = await postChatResume({
        chat_session_id: get().chatSessionId,
        realm_id: authContext.realmId,
        user_id: authContext.userId,
        approval: decision,
      });
      const lexEntry: ConversationEntry = {
        id: newId('msg'),
        role: 'lex',
        content: turn.answer.content,
        turn,
      };
      const resolvedFocus = turn.focus_matter_id ?? get().focusMatterId ?? null;
      set((state) => ({
        messages: [...state.messages, lexEntry],
        lastTurn: turn,
        focusMatterId: resolvedFocus,
        isLoading: false,
        streamingDraft: '',
      }));
      if (resolvedFocus !== get().focusMatterId) {
        writePersistedFocus(resolvedFocus);
      }
    } catch (err) {
      const message =
        err instanceof LexRuntimeError
          ? `${err.message}${err.detail ? `: ${err.detail}` : ''}`
          : err instanceof Error
            ? err.message
            : 'Failed to resume Lex interaction';
      set({ error: message, isLoading: false, streamingDraft: '' });
    }
  },

  resetSession() {
    get().attachedDocuments.forEach(revokeAttachmentPreview);
    const fresh = newId('session');
    writePersistedSessionId(fresh);
    writePersistedFocus(null);
    set({
      messages: [],
      lastTurn: null,
      isLoading: false,
      streamProgress: [],
      streamingDraft: '',
      error: null,
      chatSessionId: fresh,
      focusMatterId: null,
      attachedDocuments: [],
    });
  },

  setFocusMatter(matterId: string | null) {
    writePersistedFocus(matterId);
    set({ focusMatterId: matterId });
  },
}));
